/**
 * ?view=fotos — de ingang naar de foto's en video's van de kleden.
 *
 * De orderpagina's linken hierheen met een statische href en hoeven daardoor
 * zelf niets op te halen. Al het opzoekwerk gebeurt hier:
 *  - één kleed met media  → meteen door naar /fotos/{token}, geen tussenscherm
 *  - meerdere kleden      → een tegel per kleed
 *  - geen media           → lege staat, geen doodlopend scherm
 *
 * De tegels komen uit de media-response (`fotoLinks`), niet uit
 * `order.voorinspectie.items`. Daarmee hangt deze ingang nergens meer aan de
 * voorinspectie-vlag: een kleed met alleen een reinigingsvideo verschijnt hier
 * ook. Namen, volgnummers en maten worden bijgezet uit de voorinspectie zodra
 * die er is, want dat is de enige bron die de kleden in klantvolgorde kent.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { OrderData, VoorinspectieItem } from '../types'
import { fetchVoorinspectieThumbnails, type VoorinspectieMedia } from '../api'
import { Card, StatusHeader } from './UI'
import { trackEvent } from '../analytics'

interface FotosViewProps {
  order: OrderData
  token: string
}

interface Tegel {
  itemId: string
  href: string
  thumbnailUrl?: string
  titel: string
  maat?: string
  /** Volgorde zoals de klant de kleden elders ziet; onbekend zakt naar achteren */
  volgnummer: number
}

/** "300×200 cm" / "ø200 cm" — zelfde stille maatregel als in het inspectierapport */
function formatMaat(item?: VoorinspectieItem): string | undefined {
  if (!item) return undefined
  if (item.diameter && item.diameter > 1) return `ø${item.diameter.toString().replace('.', ',')} cm`
  if (item.lengte && item.breedte) {
    return `${item.lengte.toString().replace('.', ',')}×${item.breedte.toString().replace('.', ',')} cm`
  }
  return undefined
}

function bouwTegels(media: VoorinspectieMedia, items: VoorinspectieItem[]): Tegel[] {
  const perItem = new Map(items.map((item, i) => [item.id, { item, volgnummer: i + 1 }]))

  const lijst = Object.entries(media.fotoLinks).map(([itemId, href]) => {
    const bekend = perItem.get(itemId)
    // item.name is meestal het generieke "Vloerkleed" — dan zegt het volgnummer
    // meer dan de naam (zelfde regel als InspectieItemCard in het rapport).
    const naam = bekend?.item.name
    const generiek = !naam || naam.toLowerCase() === 'vloerkleed'
    return {
      itemId,
      href,
      thumbnailUrl: media.thumbnails[itemId],
      titel: generiek ? `Vloerkleed ${bekend?.volgnummer ?? ''}`.trim() : naam,
      maat: formatMaat(bekend?.item),
      volgnummer: bekend?.volgnummer ?? Number.MAX_SAFE_INTEGER,
    }
  })

  // De API levert op taken_at desc, wat voor de klant geen betekenis heeft.
  // Kleden zonder voorinspectie-volgnummer zakken naar achteren, op id gesorteerd
  // zodat de volgorde tussen twee bezoeken hetzelfde blijft.
  lijst.sort((a, b) => a.volgnummer - b.volgnummer || a.itemId.localeCompare(b.itemId))
  return lijst
}

/** Terug naar de orderpagina: dezelfde URL zonder ?view */
function getOrderHref(): string {
  if (typeof window === 'undefined') return '/'
  const url = new URL(window.location.href)
  url.searchParams.delete('view')
  return url.pathname + url.search
}

export function FotosView({ order, token }: FotosViewProps) {
  // null = nog aan het laden. Hangt alleen aan het token: een achtergrond-
  // refetch van de order mag deze call (en de redirect hieronder) niet opnieuw
  // afvuren.
  const [media, setMedia] = useState<VoorinspectieMedia | null>(null)
  const heeftGetrackt = useRef(false)

  useEffect(() => {
    let active = true
    fetchVoorinspectieThumbnails(token).then((result) => {
      if (active) setMedia(result)
    })
    return () => {
      active = false
    }
  }, [token])

  const items = order.voorinspectie?.items
  const tegels = useMemo(
    () => (media ? bouwTegels(media, items ?? []) : null),
    [media, items]
  )

  useEffect(() => {
    if (!tegels || heeftGetrackt.current) return
    heeftGetrackt.current = true

    trackEvent('fotos_geopend', {
      werkOrderId: order.werkOrderId,
      werkOrderNummer: order.werkOrderNummer,
      itemCount: tegels.length,
      fotosVariant: tegels.length === 0 ? 'leeg' : tegels.length === 1 ? 'direct' : 'kiezer',
    })

    // Eén kleed: dit scherm heeft dan geen keuze te bieden en zou alleen een
    // extra klik zijn. `replace` houdt de terug-knop bruikbaar — die brengt de
    // klant naar de orderpagina en niet in een lus terug naar dit scherm.
    if (tegels.length === 1) {
      window.location.replace(tegels[0].href)
    }
  }, [tegels, order.werkOrderId, order.werkOrderNummer])

  // Laden, of onderweg naar de timeline van het enige kleed
  if (tegels === null || tegels.length === 1) {
    return (
      <div>
        <StatusHeader title="Foto's van je kleed" subtitle={<>Een moment, we halen ze op.</>} />
        <Card className="mb-md">
          <div className="h-32 rounded-premium bg-surface-soft animate-pulse" />
        </Card>
      </div>
    )
  }

  if (tegels.length === 0) {
    return (
      <div>
        <StatusHeader
          title="Nog geen foto's"
          subtitle={
            <>
              Zodra we met je {(items?.length ?? 0) === 1 ? 'kleed' : 'kleden'} aan de slag gaan,
              zie je hier de foto's van binnenkomst tot resultaat. Kom gerust later nog eens terug.
            </>
          }
        />
        <a href={getOrderHref()} className="text-body-sm font-medium text-primary">
          ← Terug naar je bestelling
        </a>
      </div>
    )
  }

  return (
    <div>
      <StatusHeader
        title="Foto's van je kleden"
        subtitle={<>Kies een kleed om het te volgen van binnenkomst tot resultaat.</>}
      />

      <div className="grid grid-cols-2 gap-md mb-lg">
        {tegels.map((tegel) => (
          <a
            key={tegel.itemId}
            href={tegel.href}
            className="block bg-white rounded-premium border border-track shadow-card overflow-hidden hover:border-primary/40 transition-colors"
          >
            {tegel.thumbnailUrl ? (
              <img
                src={tegel.thumbnailUrl}
                alt={`Foto van ${tegel.titel}`}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover bg-surface-soft"
              />
            ) : (
              <div className="w-full aspect-[4/3] bg-surface-soft" />
            )}
            <div className="px-md py-sm">
              <p className="text-body-sm font-medium text-ink truncate">{tegel.titel}</p>
              {tegel.maat && <p className="text-[12px] text-ink-faint">{tegel.maat}</p>}
            </div>
          </a>
        ))}
      </div>

      <a href={getOrderHref()} className="text-body-sm font-medium text-primary">
        ← Terug naar je bestelling
      </a>
    </div>
  )
}
